/**
 * The address list the template scheduling flow works with (MD-148).
 *
 * ONE source for both consumers — the handle inference
 * (useDynamicTemplateProviderController) and the missing-data dialog
 * (TemplateScheduleDialog) — because they have to agree: a handle primed with
 * the practice address is only correct if the dialog would have offered the
 * same address, and a dialog that cannot offer the practice cannot honour
 * "a home-visit Rx may still be scheduled at the practice".
 *
 * The list is the patient's addresses PLUS the current location's address as
 * the practice option, which is the only way the practice ever appears: it
 * belongs to the location, not to the patient. Same merge NewRx and
 * AppointmentEditCard do; centralised here so the calendar flow cannot drift.
 */

import { useMemo } from "react";
import { AddressDto } from "../../../api-client";
import { usePatient } from "../../../stores/Patients/patient.queries";
import { useCurrentLocation } from "../../../stores/User/user.queries";
import { mergePracticeAddress, pickPracticeAddress } from "./templateAddress";

export interface TemplateAddresses {
  /** Patient addresses + the practice, in selection order (practice first). */
  addresses: AddressDto[];
  /**
   * The practice address, or undefined when the current location has none on
   * file. Undefined is a real state and callers must handle it: it is the one
   * case where a practice Rx cannot be primed and must ask instead.
   */
  practiceAddress: AddressDto | undefined;
}

export const useTemplateAddresses = (
  patientId: number | undefined
): TemplateAddresses => {
  const { data: patient } = usePatient(patientId ?? 0, !!patientId);
  const currentLocation = useCurrentLocation();
  const locationAddress = currentLocation?.address;

  return useMemo(() => {
    const addresses = mergePracticeAddress(
      (patient?.addresses ?? []) as AddressDto[],
      locationAddress?.id ? locationAddress : undefined
    );
    return { addresses, practiceAddress: pickPracticeAddress(addresses) };
  }, [patient, locationAddress]);
};
